import React, { ReactElement } from 'react';
import { colors, gridColumns, space } from '../../theme';
import { Flex, Box, BoxProps, FlexProps, SxStyleProp } from 'rebass';
import { Heading } from './text';

export const PageContainer: React.FC = ({ children }) => (
  <Box px={[space.one, space.one, space.eight]} pb={space.three} width="100%">
    {children}
  </Box>
);

const sidebarWidth = '13%';
export const StaticSidebar: React.FC = ({ children }) => (
  <Flex
    sx={{
      width: [0, 0, sidebarWidth],
      display: ['none', 'none', 'block'],
      height: '100%',
      top: 0,
      backgroundColor: colors.modalSidebarBlue,
      flexShrink: 0,
      position: 'fixed',
    }}
  >
    {children}
  </Flex>
);

export const StaticHeader: React.FC<{ title?: string }> = ({
  children,
  title,
}) => (
  <Flex
    justifyContent={'flex-end'}
    flexDirection={['column', 'column', 'row']}
    sx={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: ['100%', '100%', '87%'],
      alignItems: ['left', 'left', 'center'],
      px: [space.one, space.one, space.two],
      py: space.two,
      backgroundColor: colors.white,
      boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
      '& > *:not(:last-child)': {
        mr: [0, 0, space.one],
      },
    }}
  >
    {title && (
      <Heading
        sx={{ flexGrow: 1, pl: [space.one, space.one, space.six] }}
        mb={[space.one, space.one, 0]}
        level={2}
      >
        {title}
      </Heading>
    )}
    {children}
  </Flex>
);

interface BreakpointBoxProps extends Omit<BoxProps, 'css'> {
  mobile?: boolean;
  tablet?: boolean;
  desktop?: boolean;
}
export const BreakpointBox: React.FC<BreakpointBoxProps> = ({
  children,
  mobile = false,
  tablet = false,
  desktop = false,
  ...props
}) => {
  const breakArray = [
    mobile ? 'block' : 'none',
    tablet ? 'block' : 'none',
    desktop ? 'block' : 'none',
  ];

  return (
    <Box display={breakArray} {...props}>
      {children}
    </Box>
  );
};

export const ModalFormLayout: React.FC<{
  sidebarChildren?: ReactElement;
  headerChildren: ReactElement;
  title?: string;
}> = ({ children, title, sidebarChildren, headerChildren }) => (
  <>
    <ModalPageContainer
      styles={{
        marginLeft: [0, 0, sidebarWidth],
        width: ['100%', '100%', `calc(100% - ${sidebarWidth})`],
        marginTop: [space.one, space.one, gridColumns(2)],
      }}
    >
      {title && (
        <BreakpointBox
          mobile={true}
          tablet={true}
          mb={[space.two, space.two, 0]}
        >
          <Heading level={2} mb={[space.one, space.one, 0]}>
            {title}
          </Heading>
        </BreakpointBox>
      )}
      {children}
      <BreakpointBox
        mobile={true}
        tablet={true}
        mb={[space.two, space.two, 0]}
        mt={space.half}
      >
        {headerChildren}
      </BreakpointBox>
    </ModalPageContainer>
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'hidden',
      }}
    >
      <BreakpointBox desktop={true}>
        <StaticHeader title={title}>{headerChildren}</StaticHeader>
      </BreakpointBox>
      <StaticSidebar>{sidebarChildren}</StaticSidebar>
    </Box>
  </>
);

export const ModalPageContainer: React.FC<{
  styles: SxStyleProp;
}> = props => (
  <Box px={[space.one, space.one, space.eight]} sx={{ ...props.styles }}>
    {props.children}
  </Box>
);

export const CenteredStack: React.FC = props => (
  <Flex alignItems="center" flexDirection="column" {...props}>
    {props.children}
  </Flex>
);

export const MobileColumnDesktopRow: React.FC = ({ children }) => (
  <Flex
    alignItems={'start'}
    flexDirection={['column', 'row', 'row']}
    width={'100%'}
  >
    {children}
  </Flex>
);

export const MobileColumnDesktopRowCell: React.FC = ({ children }) => (
  <Box
    width={['100%', '50%', '50%']}
    mb={[space.two, space.one, space.one]}
    mr={[0, space.half, space.half]}
  >
    {children}
  </Box>
);

type JustifiedRowProps = Pick<FlexProps, 'alignItems' | 'flexWrap'>;
export const JustifiedRow: React.FC<JustifiedRowProps> = props => {
  return (
    <Flex
      alignItems="center"
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
      {...props}
    >
      {props.children}
    </Flex>
  );
};
